import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, already_verified
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Geçersiz doğrulama linki. Token bulunamadı.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API}/verify-email`, {
        params: { token }
      });

      if (response.data.already_verified) {
        setStatus('already_verified');
        setMessage('Email adresiniz zaten doğrulanmış.');
      } else {
        setStatus('success');
        setMessage(response.data.message || 'Email adresiniz başarıyla doğrulandı!');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Email doğrulama başarısız oldu.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              ONXFLOW
            </h1>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Email Doğrulanıyor...
              </h2>
              <p className="text-gray-600">
                Lütfen bekleyin, email adresiniz doğrulanıyor.
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Email Doğrulandı! ✅
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Giriş Sayfasına Git
              </Button>
            </div>
          )}

          {/* Already Verified State */}
          {status === 'already_verified' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Zaten Doğrulanmış
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Giriş Sayfasına Git
              </Button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center py-8">
              <XCircle className="w-16 h-16 mx-auto text-red-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Doğrulama Başarısız ❌
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full"
                >
                  Giriş Sayfasına Git
                </Button>
                <p className="text-sm text-gray-500">
                  Sorun devam ederse lütfen yöneticinizle iletişime geçin.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            © 2024 ONXFLOW. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
}
